import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Play, Sparkles, Video, Coins, Loader2, Clock, Film, Eye } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const HERO_TILES = [
  {
    id: "product",
    title: "PRODUCT PHOTOSHOOT",
    subtitle: "Generate stunning AI product photography",
    tag: "Product",
    fallbackVideo: "https://prysh-ai-media.s3.ap-south-1.amazonaws.com/dashboard/hero_product.mp4",
  },
  {
    id: "ugc",
    title: "EXPLORE TEMPLATES",
    subtitle: "Browse ready-made video ad templates",
    tag: "UGC",
    fallbackVideo: "https://prysh-ai-media.s3.ap-south-1.amazonaws.com/dashboard/hero_ugc.mp4",
  },
  {
    id: "custom",
    title: "CREATE YOUR OWN",
    subtitle: "Build a custom video ad from scratch",
    tag: null,
    fallbackVideo: "https://prysh-ai-media.s3.ap-south-1.amazonaws.com/dashboard/hero_custom.mp4",
  },
];

function HeroTile({ tile, videoUrl, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative w-full aspect-video rounded-2xl overflow-hidden group cursor-pointer text-left"
      data-testid={`hero-tile-${tile.id}`}
    >
      {videoUrl ? (
        <video
          src={videoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <div className="absolute inset-0 bg-slate-800" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-center gap-2.5 mb-1">
          <h2 className="text-2xl font-bold text-white tracking-tight uppercase">{tile.title}</h2>
          <ArrowRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform duration-300" />
        </div>
        <p className="text-sm text-white/70 font-normal">{tile.subtitle}</p>
      </div>
    </button>
  );
}

function TemplateCard({ template, onUse, onPreview }) {
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current) videoRef.current.play().catch(() => {});
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group bg-white rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-xl transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`dashboard-template-card-${template.id}`}
    >
      <div className="aspect-[9/16] relative bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
        {["hero_shot", "ugc_avatar4"].includes(template.template_type) && (
          <div className="absolute top-2 right-2 z-10 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            PRO
          </div>
        )}
        {template.sample_video ? (
          <>
            <video ref={videoRef} src={template.sample_video} className="w-full h-full object-cover" loop muted playsInline />
            {!isHovering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-5 h-5 text-slate-700 ml-0.5" />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Video className="w-10 h-10 text-muted-foreground" />
          </div>
        )}
        <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-3 transition-opacity duration-300 ${isHovering ? "opacity-100" : "opacity-0"}`}>
          <Button onClick={() => onUse(template)} className="w-full mb-2" size="sm" data-testid={`use-template-btn-${template.id}`}>
            <Sparkles className="w-4 h-4 mr-1.5" />
            Use Template
          </Button>
          <Button onClick={() => onPreview(template)} variant="secondary" className="w-full" size="sm" data-testid={`preview-template-btn-${template.id}`}>
            <Eye className="w-4 h-4 mr-1.5" />
            Preview
          </Button>
        </div>
      </div>
      {/* Info bar */}
      <div className="px-3 py-2 flex items-center justify-between text-xs text-muted-foreground border-t border-border">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          ~{template.video_duration || (template.scene_count ? template.scene_count * 5 : template.template_type === 'ugc_2scene' ? 12 : template.template_type === 'ugc_sora2' ? 12 : 6)}s
        </span>
        <span className="flex items-center gap-1">
          <Film className="w-3 h-3" />
          {template.scene_count || (template.template_type === 'ugc_2scene' ? 2 : 1)} scene{(template.scene_count || (template.template_type === 'ugc_2scene' ? 2 : 1)) > 1 ? 's' : ''}
        </span>
        {template.credits_cost > 0 && (
          <span className="flex items-center gap-1 text-amber-600 font-medium">
            <Coins className="w-3 h-3" />
            {template.credits_cost}
          </span>
        )}
      </div>
    </div>
  );
}

function TemplateSection({ title, templates, loading, onViewAll, onUse, onPreview, testId }) {
  const [activeTag, setActiveTag] = useState(null);

  // Extract unique tags from this section's templates
  const allTags = [...new Set(templates.flatMap(t => (t.tags || []).map(tag => tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase())))].sort();

  const filtered = activeTag
    ? templates.filter(t => (t.tags || []).some(tag => tag.toLowerCase() === activeTag.toLowerCase()))
    : templates;

  return (
    <div className="mb-10" data-testid={`section-${testId}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
          onClick={onViewAll}
          data-testid={`view-all-${testId}-btn`}
        >
          View all <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid={`tags-${testId}`}>
          <button
            onClick={() => setActiveTag(null)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              !activeTag ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/80"
            }`}
          >
            All
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag === activeTag ? null : tag)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                activeTag?.toLowerCase() === tag.toLowerCase() ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/80"
              }`}
              data-testid={`tag-${testId}-${tag.toLowerCase()}`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map((t) => (
            <TemplateCard key={t.id} template={t} onUse={onUse} onPreview={onPreview} />
          ))}
        </div>
      ) : (
        <div className="text-center py-10 text-muted-foreground rounded-xl border border-dashed border-border">
          <Video className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{templates.length > 0 ? "No templates match this tag" : "Coming soon"}</p>
        </div>
      )}
    </div>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingProject, setCreatingProject] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/templates?status=published`, { withCredentials: true });
      const all = res.data.templates || res.data || [];
      setTemplates(all);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTileClick = async (tile) => {
    if (tile.id === "product") {
      navigate("/photoshoot");
    } else if (tile.id === "ugc") {
      navigate("/templates");
    } else if (tile.tag) {
      navigate(`/templates?tag=${tile.tag}`);
    } else {
      // Redirect to Marketing Studio
      navigate("/marketing-studio");
    }
  };

  const handleUseTemplate = (template) => {
    navigate("/templates", { state: { useTemplateId: template.id } });
  };

  const handlePreviewTemplate = (template) => {
    navigate("/templates", { state: { previewTemplateId: template.id } });
  };

  // Filter templates into 3 sections by template_type
  const ugcTypes = ["ugc", "ugc_avatar4", "ugc_sora2", "ugc_2scene"];
  const ugcTemplates = templates.filter(t => t.status === "published" && ugcTypes.includes(t.template_type));
  const productTemplates = templates.filter(t => t.status === "published" && t.template_type === "product_video");
  const heroTemplates = templates.filter(t => t.status === "published" && t.template_type === "hero_shot");

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight">
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-muted-foreground mt-1">What would you like to create today?</p>
        </div>

        {/* Marketing Studio Banner */}
        <div
          onClick={() => navigate("/marketing-studio")}
          className="mb-8 rounded-2xl overflow-hidden bg-gradient-to-r from-slate-900 to-slate-800 border border-white/10 p-6 flex items-center justify-between cursor-pointer group hover:border-primary/30 transition-colors"
          data-testid="marketing-studio-banner"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] bg-primary text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">New</span>
              <span className="text-xs text-white/40 font-medium uppercase tracking-wider">Marketing Studio</span>
            </div>
            <h3 className="text-lg font-bold text-white">Turn Any Product Into a Video Ad</h3>
            <p className="text-sm text-white/50 mt-1">AI-powered video generation in minutes</p>
          </div>
          <Button
            className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 gap-2 shrink-0 group-hover:shadow-lg group-hover:shadow-primary/20 transition-shadow"
            onClick={(e) => { e.stopPropagation(); navigate("/marketing-studio"); }}
            data-testid="marketing-studio-try-now"
          >
            Try Now <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Hero Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {HERO_TILES.map((tile) => (
            <HeroTile
              key={tile.id}
              tile={tile}
              videoUrl={tile.fallbackVideo}
              onClick={() => handleTileClick(tile)}
            />
          ))}
        </div>

        {/* Loading overlay for project creation */}
        {creatingProject && (
          <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 flex items-center gap-3 shadow-xl">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-medium">Creating your project...</span>
            </div>
          </div>
        )}

        {/* Social Media & UGC Section */}
        <TemplateSection
          title="Social Media & UGC"
          templates={ugcTemplates}
          loading={loading}
          onViewAll={() => navigate("/templates?tag=UGC")}
          onUse={handleUseTemplate}
          onPreview={handlePreviewTemplate}
          testId="ugc"
        />

        {/* Product Videos Section */}
        <TemplateSection
          title="Product Videos"
          templates={productTemplates}
          loading={loading}
          onViewAll={() => navigate("/templates?tag=Product")}
          onUse={handleUseTemplate}
          onPreview={handlePreviewTemplate}
          testId="product"
        />

        {/* Hero Shots Section */}
        <TemplateSection
          title="Hero Shots"
          templates={heroTemplates}
          loading={loading}
          onViewAll={() => navigate("/templates?tag=Hero")}
          onUse={handleUseTemplate}
          onPreview={handlePreviewTemplate}
          testId="hero"
        />
      </div>
    </div>
  );
};

export default Dashboard;
